import {ActivatedRouteSnapshot, Resolve, RouterStateSnapshot} from '@angular/router';

import {map} from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    ServerResponse,
    HttpStatusCodesEnum,
    ReportsService,
    ClientUserReportInterface,
    ClientUserReportModel,
    ClientUserFilterModel
} from '@spa/api';

import {FilterControl, Helper} from '../../../shared/classes';
import {ReportTableConfigInterface} from '../../../shared/components';

import {ClientUsersResolverDataInterface} from '../interfaces';
import {ClientUsersReportResolverErrorStatesEnum} from '../enums';
import {ComponentStorageKeyProvider} from 'appShared/providers';
import {CacheCollectionService} from 'appShared/services';

export abstract class ClientUsersReportResolver implements Resolve<ServerResponse<ClientUsersResolverDataInterface>> {
    protected componentStorageKeyProvider: ComponentStorageKeyProvider;
    // tslint:disable-next-line:max-line-length
    protected constructor(protected reportsService: ReportsService, protected cacheCollectionService: CacheCollectionService, protected storageId: string) {
        this.componentStorageKeyProvider = new ComponentStorageKeyProvider(storageId);
    }

    private mapData(data: ClientUserReportInterface[]): ClientUserReportModel[] {
        return data.map((item: ClientUserReportInterface) => new ClientUserReportModel(item));
    }

    protected abstract getDefaultFilterModel(): ClientUserFilterModel;

    protected abstract getFilterControls(): FilterControl[];

    protected abstract getDataTableConfig(): ReportTableConfigInterface;

    /**
     * Keeps the cached state of a report that is resolved for several entities
     * separated per entity, so one entity never restores the state of another.
     */
    protected setStorageScope(scope: string | number): void {
        this.componentStorageKeyProvider = new ComponentStorageKeyProvider(this.storageId, scope);
    }

    /**
     * Last line of defence against a cached filter model that was built for another
     * entity - it must never be able to re-scope the request (CRM-5197).
     */
    protected applyRouteScope(filter: ClientUserFilterModel): ClientUserFilterModel {
        return filter;
    }

    resolve(
        route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): ServerResponse<ClientUsersResolverDataInterface> {
        const filterControls = this.getFilterControls();
        const filterCache = this.cacheCollectionService.getItem(this.componentStorageKeyProvider.getFilterModelKey())?.data;
        const filter = filterCache ? this.applyRouteScope(filterCache) : this.getDefaultFilterModel();

        return this.reportsService.getClientUsersReport(filter)
            .pipe(
                map((response: ApiResponse<ApiError[] | ClientUserReportInterface[]>) => {
                    if (response.status !== HttpStatusCodesEnum.Ok) {
                        return Helper.errorsHelper().createErrorResponse(
                            response,
                            ClientUsersReportResolverErrorStatesEnum.GetReportFailed
                        );
                    }

                    const data = response.data;


                    let modelsCollection = [];

                    if (data !== null) {
                        modelsCollection = this.mapData(data as ClientUserReportInterface[]);
                    }

                    return new ApiResponse<ClientUsersResolverDataInterface>(HttpStatusCodesEnum.Ok, {
                        filterControls,
                        dataTableConfig: this.getDataTableConfig(),
                        modelsCollection,
                    });
                })
            );
    }
}
